// Shared AI progress banner — step-by-step interactive indicator for long
// AI operations (digest generation, translation, SKU/AI import, extraction…).
//
// Two modes:
//   1. Server-driven: pass `progress = {step, total, label}` from a polling API
//      that reports real progress. Elapsed time comes from `since` (ISO).
//   2. Client-simulated: if `progress` is null, we advance through `steps`
//      on a schedule so the user sees real motion. Useful for endpoints
//      that block synchronously (no server events). Auto-slows toward end.
//
// Auto-detection of "long context": when elapsed exceeds `slowAfterMs`,
// we show an "この処理は時間がかかっています…" hint so users know to be patient.
//
// UI: violet by default; pass tone="indigo" or "amber" for variants.
import { useEffect, useMemo, useState } from 'react';
import { CircleNotch, CheckCircle, Circle, Warning } from '@phosphor-icons/react';

const TONES = {
  violet: {
    border: 'border-violet-200', bg: 'bg-violet-50', title: 'text-violet-900',
    meta: 'text-violet-700', sep: 'text-violet-300', bar: 'bg-violet-200/70',
    barDone: 'bg-violet-600', barActive: 'bg-violet-500',
    itemDone: 'text-violet-700', itemActive: 'text-violet-900', itemIdle: 'text-violet-400',
    spinner: 'text-violet-600', foot: 'text-violet-600/80',
  },
  indigo: {
    border: 'border-indigo-200', bg: 'bg-indigo-50', title: 'text-indigo-900',
    meta: 'text-indigo-700', sep: 'text-indigo-300', bar: 'bg-indigo-200/70',
    barDone: 'bg-indigo-600', barActive: 'bg-indigo-500',
    itemDone: 'text-indigo-700', itemActive: 'text-indigo-900', itemIdle: 'text-indigo-400',
    spinner: 'text-indigo-600', foot: 'text-indigo-600/80',
  },
  amber: {
    border: 'border-amber-200', bg: 'bg-amber-50', title: 'text-amber-900',
    meta: 'text-amber-700', sep: 'text-amber-300', bar: 'bg-amber-200/70',
    barDone: 'bg-amber-600', barActive: 'bg-amber-500',
    itemDone: 'text-amber-700', itemActive: 'text-amber-900', itemIdle: 'text-amber-400',
    spinner: 'text-amber-600', foot: 'text-amber-600/80',
  },
  teal: {
    border: 'border-teal-200', bg: 'bg-teal-50', title: 'text-teal-900',
    meta: 'text-teal-700', sep: 'text-teal-300', bar: 'bg-teal-200/70',
    barDone: 'bg-teal-600', barActive: 'bg-teal-500',
    itemDone: 'text-teal-700', itemActive: 'text-teal-900', itemIdle: 'text-teal-400',
    spinner: 'text-teal-600', foot: 'text-teal-600/80',
  },
};

const fmtElapsed = (ms) => {
  const sec = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
};

export function AiProgressBanner({
  progress = null,                 // {step, total, label} — server-driven
  steps,                            // string[] — client labels (also used for total when no progress)
  since,                            // ISO string — start time (fallback: mount time)
  tone = 'violet',
  defaultLabel = 'AI処理を実行しています…',
  footer = '他のページに移動してもバックグラウンドで続行されます（通常 1〜2 分）。',
  testIdPrefix = 'ai-progress',
  slowAfterMs = 90 * 1000,          // show "時間がかかっています" hint after 90s
  onCancel = null,                  // optional () => Promise — shows a 中止 button
  cancelLabel = '中止',
  // Client-simulated stepping (used only when progress is null)
  autoAdvance = true,
  simulatedStepMs = 15 * 1000,      // 15s per simulated step
}) {
  const [now, setNow] = useState(Date.now());
  const [simStep, setSimStep] = useState(1);
  const startAt = useMemo(() => (since ? new Date(since).getTime() : Date.now()), [since]);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Client-side simulated step ticker — only when no server-side progress.
  useEffect(() => {
    if (progress || !autoAdvance) return;
    const total = steps.length || 4;
    const t = setInterval(() => {
      setSimStep((s) => {
        // Never auto-jump to the last step — hold until server confirms.
        const cap = Math.max(total - 1, 1);
        return Math.min(s + 1, cap);
      });
    }, simulatedStepMs);
    return () => clearInterval(t);
  }, [progress, autoAdvance, simulatedStepMs, steps.length]);

  const t = TONES[tone] || TONES.violet;
  const total = progress?.total || steps.length || 4;
  const step = progress
    ? Math.min(Math.max(progress.step || 0, 0), total)
    : Math.min(Math.max(simStep, 1), total);
  const label = progress?.label
    || steps[Math.max(step - 1, 0)]
    || defaultLabel;
  // When the backend runs steps in parallel it sends `done: ['engineer', ...]`
  // — an array of finished step KEYS or LABEL substrings. An item is "done"
  // if either (a) its 1-based index is below `step` (sequential mode) or (b)
  // its label matches an entry in `done` (parallel mode).
  const doneList = Array.isArray(progress?.done) ? progress.done : [];
  const isDone = (i) => (i + 1 < step) || doneList.some((k) => {
    const key = String(k || '').toLowerCase();
    const label = String(steps[i] || '').toLowerCase();
    return label.includes(key) || key.includes(label) || key === label;
  });

  const elapsed = Math.max(0, now - startAt);
  const isSlow = elapsed >= slowAfterMs;

  return (
    <div className={`rounded-xl border ${t.border} ${t.bg} px-5 py-4`} data-testid={`${testIdPrefix}-banner`}>
      <div className="flex flex-wrap items-center gap-3">
        <CircleNotch size={18} className={`${t.spinner} animate-spin shrink-0`} />
        <p className={`text-[13px] font-semibold ${t.title} flex-1 min-w-[200px]`} data-testid={`${testIdPrefix}-label`}>
          {label}
        </p>
        <div className={`text-[11px] font-semibold ${t.meta} shrink-0 flex items-center gap-2`}>
          <span data-testid={`${testIdPrefix}-step`}>ステップ {Math.max(step, 1)} / {total}</span>
          <span className={t.sep}>|</span>
          <span data-testid={`${testIdPrefix}-elapsed`}>経過 <span className="tabular-nums">{fmtElapsed(elapsed)}</span></span>
        </div>
      </div>
      <div className="mt-3 flex gap-1.5" data-testid={`${testIdPrefix}-bar`}>
        {steps.map((_, i) => (
          <div key={i} className={`h-2 flex-1 rounded-full ${t.bar} overflow-hidden`}>
            {isDone(i) && <div className={`h-full w-full ${t.barDone} rounded-full`} />}
            {!isDone(i) && i + 1 === step && <div className={`h-full w-2/3 ${t.barActive} rounded-full animate-pulse`} />}
          </div>
        ))}
      </div>
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
        {steps.map((sl, i) => {
          const done = isDone(i);
          const active = !done && i + 1 === step;
          return (
            <div key={i} data-testid={`${testIdPrefix}-item-${i + 1}`}
              className={`flex items-center gap-1.5 text-[11px] ${
                done ? t.itemDone : active ? `${t.itemActive} font-semibold` : t.itemIdle}`}>
              {done ? <CheckCircle size={13} weight="fill" className="text-emerald-500 shrink-0" />
                : active ? <CircleNotch size={13} className={`animate-spin ${t.spinner} shrink-0`} />
                  : <Circle size={13} className="shrink-0" />}
              <span className="truncate">{sl}</span>
            </div>
          );
        })}
      </div>
      <div className="mt-2.5 flex items-center justify-between gap-3">
        <p className={`text-[11px] ${t.foot} flex items-center gap-1.5 flex-1 min-w-0`}>
          {isSlow && <Warning size={12} weight="fill" className="shrink-0" />}
          <span className="truncate">
            {isSlow ? 'この処理は時間がかかっています。中断せずにお待ちください。' : footer}
          </span>
        </p>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            data-testid={`${testIdPrefix}-cancel-btn`}
            className={`shrink-0 text-[11px] font-semibold px-3 py-1 rounded-full border ${t.border} bg-white/70 hover:bg-white ${t.title} transition-colors`}>
            {cancelLabel}
          </button>
        )}
      </div>
    </div>
  );
}

export default AiProgressBanner;
